import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { resetPassword, loginUser } from '../../services/apiService';
import $ from "jquery";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const { token: rawToken } = useParams();
  const navigate = useNavigate();
  
  // Parse token and email from the malformed URL structure
  let token = rawToken;
  let email = searchParams.get('email');
  
  // Handle case where token contains &email= due to malformed URL
  if (rawToken && rawToken.includes('&email=')) {
    const parts = rawToken.split('&email=');
    token = parts[0];
    email = email || parts[1]; // Use from query param if available, otherwise from token
  }

  // Debug logging
  console.log('Reset Password Page - URL params:', {
    rawToken,
    token,
    email,
    fullUrl: window.location.href,
    pathname: window.location.pathname,
    search: window.location.search
  });

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  // Redirect to 404 if token or email is missing
  useEffect(() => {
    console.log('Validation check:', { token: !!token, email: !!email });
    if (!token || !email) {
      console.log('Missing required params, redirecting to 404');
      navigate('/not-found', { replace: true });
    }
  }, [token, email, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await resetPassword({
        password: formData.password,
        token
      });
      
      // Auto-login user with new password
      try {
        const loginResponse = await loginUser({
          email: email,
          password: formData.password
        });
        
        console.log('Auto-login after password reset:', loginResponse);
        
        if (loginResponse?.token) {
          // Create user object and store authentication data
          const userData = {
            email: loginResponse.email,
            name: loginResponse.username,
            token: loginResponse.token,
            id: loginResponse.id,
          };

          // Store user data in localStorage
          localStorage.setItem('accessToken', loginResponse.token);
          localStorage.setItem('user', JSON.stringify(userData));

          // Trigger a custom event to notify navbar of login
          window.dispatchEvent(new CustomEvent('userLogin', { detail: userData }));
          
          console.log('User automatically logged in after password reset');
        }
      } catch (loginError) {
        console.error('Auto-login failed after password reset:', loginError);
        // Continue with success flow even if auto-login fails
      }
      
      setResetSuccess(true);
      
      // Redirect to home page after 3 seconds
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 3000);
    } catch (error) {
      console.error('Reset password error:', error);
      // Show the actual API message if available, otherwise show generic error
      const errorMessage = error?.response?.data?.message || error?.message || 'Something went wrong. Please try again.';
      setErrors({ 
        submit: errorMessage 
      });
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setShowPassword(prev => !prev);
    } else {
      setShowConfirmPassword(prev => !prev);
    }
  };

  // If token or email is missing, the useEffect will redirect to 404
  if (!token || !email) {
    return null; // Return null while redirecting
  }

  // Success state
  if (resetSuccess) {
    return (
      <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
          <h3>Password Reset Successful!</h3>
          <p className="text-muted mb-4">
            Your password has been successfully reset and you are now logged in.
          </p>
          <p className="text-muted">
            Redirecting you to the home page in a few seconds...
          </p>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="container-fluid py-5">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-4">
          <div className="card shadow">
            <div className="card-body p-4">
              <div className="text-center mb-4">
                <h3>Reset Your Password</h3>
                <p className="text-muted">Enter your new password below</p>
                {email && (
                  <p className="text-muted small">for <strong>{email}</strong></p>
                )}
              </div>

              <form onSubmit={handleSubmit}>
                {/* New Password Field */}
                <div className="mb-3">
                  <label htmlFor="password" className="form-label">New Password</label>
                  <div className="position-relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className={`form-control pe-5 ${errors.password ? 'is-invalid' : ''}`}
                      id="password"
                      name="password"
                      placeholder="Enter your new password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                    />
                    <span
                      className="position-absolute top-50 end-0 translate-middle-y me-3"
                      style={{ cursor: 'pointer' }}
                      onClick={() => togglePasswordVisibility('password')}
                    >
                      <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                    </span>
                    {errors.password && (
                      <div className="invalid-feedback">{errors.password}</div>
                    )}
                  </div>
                  <small className="form-text text-muted">
                    Password must be at least 8 characters long
                  </small>
                </div>

                {/* Confirm Password Field */}
                <div className="mb-4">
                  <label htmlFor="confirmPassword" className="form-label">Confirm New Password</label>
                  <div className="position-relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      className={`form-control pe-5 ${errors.confirmPassword ? 'is-invalid' : ''}`}
                      id="confirmPassword"
                      name="confirmPassword"
                      placeholder="Confirm your new password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                    />
                    <span
                      className="position-absolute top-50 end-0 translate-middle-y me-3"
                      style={{ cursor: 'pointer' }}
                      onClick={() => togglePasswordVisibility('confirmPassword')}
                    >
                      <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                    </span>
                    {errors.confirmPassword && (
                      <div className="invalid-feedback">{errors.confirmPassword}</div>
                    )}
                  </div>
                </div>

                {/* Submit Error */}
                {errors.submit && (
                  <div className="alert alert-danger" role="alert">
                    {errors.submit}
                  </div>
                )}

                {/* Submit Button */}
                <div className="d-grid">
                  <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Resetting Password...
                      </>
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </div>
              </form>

              <div className="text-center mt-4">
                <button 
                  className="btn btn-link text-decoration-none"
                  onClick={() => navigate('/', { replace: true })}
                >
                  Back to Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
